import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Camera,
  ChevronDown,
  Clock,
  DollarSign,
  Gauge,
  Layers,
  Loader2,
  Radar,
  Target,
  TrendingDown,
  Users,
  X,
} from 'lucide-react';
import { CardData } from '../types';
import { KaizenOverride, SimulationShiftPreset, useSimulation } from '../context/SimulationContext';
import { optimizeWorkflow } from '../utils/workflowOptimization';
import OptimizationSummaryCard, { OptimizationSummaryData } from './OptimizationSummaryCard';
import { generateExecutiveReport } from '../utils/generateExecutiveReport';
import { useProjectStore } from '../store/useProjectStore';

type SimulationTab = 'operation' | 'analytics';
type Horizon = '1m' | '3m';

interface SimulationSidebarProps {
  cards: CardData[];
  selectedCard: CardData | null;
  metrics?: any;
  insightSuggestion?: {
    id: number;
    focusResource?: string;
    focusCardId?: string;
    suggestedExtraFte?: number;
    suggestedWaitReductionPercent?: number;
  } | null;
  onClearSelection: () => void;
  onClose: () => void;
  onUpdateCardData: (id: string, data: Partial<CardData>) => void;
  onFocusBottleneck?: (cardId: string) => void;
  onScenarioSaved?: (scenarioName: string) => void;
  onOptimizationFeedback?: (kind: 'success' | 'warning' | 'error', message: string) => void;
}

const HORIZON_LABELS: Record<Horizon, string> = {
  '1m': '1 Mês',
  '3m': '3 Meses',
};

const HORIZON_DAYS: Record<Horizon, number> = {
  '1m': 22,
  '3m': 66,
};
const HORIZON_TO_SIM = {
  '1m': 'daily',
  '3m': 'monthly',
} as const;
const SIM_TO_HORIZON = {
  daily: '1m',
  weekly: '1m',
  monthly: '3m',
} as const;

const SHIFT_LABELS: Record<SimulationShiftPreset, string> = {
  '1_shift': '1 Turno',
  '2_shifts': '2 Turnos',
  'plus_2h': '+2h Extra',
};

const SHIFT_MINUTES: Record<SimulationShiftPreset, number> = {
  '1_shift': 480,
  '2_shifts': 960,
  'plus_2h': 600,
};

const TICKET_MEDIO = 1800;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const normalizeOverride = (override?: KaizenOverride) => ({
  fte: Math.max(0, Number(override?.fte ?? override?.extraFTE ?? 0)),
  rework: clamp(Number(override?.rework ?? override?.reworkReduction ?? 0), 0, 100),
});

const toFactoryMinutes = (value: unknown, unit: unknown, fallbackUnit: 's' | 'min' | 'h' = 's') => {
  const numericValue = Number(value) || 0;
  const resolvedUnit = typeof unit === 'string' && unit.length > 0 ? unit : fallbackUnit;
  if (['dias', 'dia', 'd'].includes(resolvedUnit)) return numericValue * 1440;
  if (['horas', 'hora', 'h'].includes(resolvedUnit)) return numericValue * 60;
  if (['minutos', 'minuto', 'min'].includes(resolvedUnit)) return numericValue;
  return numericValue / 60;
};

const getNodeTimes = (card: CardData, isFactoryMap = false) => {
  if (isFactoryMap && (card as any).vsmIcon === 'processo') {
    const ct = toFactoryMinutes((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit, 's');
    return { tra: ct, tp: ct };
  }

  const tra = Math.max(0, Number(card.tt ?? 0));
  const rawTp = Number(card.lt ?? card.tp ?? card.tt ?? 0);
  const tp = Math.max(tra, Number.isFinite(rawTp) ? rawTp : tra);
  return { tra, tp };
};

const fmtCurrency = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const getFactoryDailyDemand = (cards: CardData[]) => {
  const clientCard = cards.find(card => {
    const extType = String((card as any).extType ?? '').toUpperCase();
    return card.tipo === 'card_final' || extType === 'CLIENTE' || (card as any).vsmIcon === 'cliente';
  });
  const rawDemand = Number((clientCard as any)?.vsmDemanda ?? (clientCard as any)?.entrada ?? 0);
  const demandUnit = String((clientCard as any)?.unidadeDemanda ?? (clientCard as any)?.vsmDemandaUnidade ?? '/dia').toLowerCase();
  if (rawDemand <= 0) return 0;
  if (demandUnit.includes('mês') || demandUnit.includes('mes') || demandUnit.includes('/ms')) return rawDemand / 20;
  if (demandUnit.includes('hora')) return rawDemand * 8;
  return rawDemand;
};

const getProcessCards = (cards: CardData[], isFactoryMap = false) =>
  cards.filter(
    (card) =>
      card.status === 'ativo' &&
      card.tipo !== 'card_inicial' &&
      card.tipo !== 'card_final' &&
      card.tipo !== 'decisao' &&
      (!isFactoryMap || (card as any).vsmIcon === 'processo'),
  );

const SimulationSidebar: React.FC<SimulationSidebarProps> = ({
  cards,
  selectedCard,
  metrics,
  insightSuggestion,
  onClearSelection,
  onClose,
  onUpdateCardData,
  onFocusBottleneck,
  onScenarioSaved,
  onOptimizationFeedback,
}) => {
  const simulation = useSimulation();
  const mapType = useProjectStore(state => state.mapType);
  const viewMode = useProjectStore(state => state.viewMode);
  const isFactoryMap = mapType === 'FACTORY';
  const [activeTab, setActiveTab] = useState<SimulationTab>('operation');
  const [horizon, setHorizon] = useState<Horizon>(SIM_TO_HORIZON[simulation.horizon] ?? '1m');
  const [horizonOpen, setHorizonOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationSummary, setOptimizationSummary] = useState<OptimizationSummaryData | null>(null);
  const [optimizedOverridesSnapshot, setOptimizedOverridesSnapshot] = useState<Record<string, KaizenOverride>>({});
  const lastAppliedSuggestionRef = useRef<number | null>(null);
  const suggestionBaselineOverridesRef = useRef<Record<string, KaizenOverride> | null>(null);

  const startDemand = useMemo(
    () => Math.max(1, isFactoryMap ? getFactoryDailyDemand(cards) || 100 : Number(cards.find((card) => card.tipo === 'card_inicial')?.entrada ?? 100)),
    [cards, isFactoryMap],
  );

  useEffect(() => {
    simulation.setIsActive(true);
    return () => {
      simulation.setIsActive(false);
    };
  }, [simulation.setIsActive]);

  useEffect(() => {
    if (!simulation.isActive) {
      suggestionBaselineOverridesRef.current = null;
      lastAppliedSuggestionRef.current = null;
    }
  }, [simulation.isActive]);

  useEffect(() => {
    if (simulation.demand <= 0) {
      simulation.setDemand(startDemand);
      simulation.setSimulatedDemand(startDemand);
    }
  }, [simulation.demand, simulation.setDemand, simulation.setSimulatedDemand, startDemand]);

  const demand = simulation.demand > 0 ? simulation.demand : startDemand;
  const shiftMinutes = SHIFT_MINUTES[simulation.shiftPreset];
  const batchSize = Math.max(1, Number(simulation.batchSize || 1));
  const processCards = useMemo(() => getProcessCards(cards, isFactoryMap), [cards, isFactoryMap]);

  const setGlobalDemand = useCallback(
    (nextDemand: number) => {
      const value = clamp(Math.round(nextDemand), 1, 10000);
      simulation.setDemand(value);
      simulation.setSimulatedDemand(value);
    },
    [simulation],
  );

  const setGlobalBatchSize = useCallback(
    (nextValue: number) => {
      simulation.setBatchSize(clamp(Math.round(nextValue || 1), 1, 999));
    },
    [simulation],
  );

  const calculateSnapshotMetrics = useCallback(
    (overridesMap: Record<string, KaizenOverride>) => {
      if (processCards.length === 0) {
        return {
          leadTimeMinutes: 0,
          pcePercent: 0,
          nvaCost: 0,
          deliveryCapacityPerDay: 0,
        };
      }

      const hourlyNvaCost = 150;
      let totalTra = 0;
      let totalTp = 0;
      let minCapacity = Number.POSITIVE_INFINITY;

      processCards.forEach((card) => {
        const { tra, tp } = getNodeTimes(card, isFactoryMap);
        const wait = Math.max(0, tp - tra);
        const normalized = normalizeOverride(overridesMap[card.id]);
        const waitReduction = normalized.rework / 100;
        const adjustedWait = wait * (1 - waitReduction);
        const adjustedTp = tra + adjustedWait;

        const baseFte = Math.max(1, Number(card.pessoasSimultaneas ?? 1));
        const adjustedTraForCapacity = Math.max(0.01, tra * (1 - waitReduction));
        const dailyCapacity = (shiftMinutes * (baseFte + normalized.fte)) / adjustedTraForCapacity;

        totalTra += tra;
        totalTp += adjustedTp;
        minCapacity = Math.min(minCapacity, dailyCapacity);
      });

      const totalWait = Math.max(0, totalTp - totalTra);
      const pcePercent = totalTp > 0 ? (totalTra / totalTp) * 100 : 0;

      return {
        leadTimeMinutes: totalTp,
        pcePercent,
        nvaCost: (totalWait / 60) * hourlyNvaCost,
        deliveryCapacityPerDay: Number.isFinite(minCapacity) ? minCapacity : 0,
      };
    },
    [isFactoryMap, processCards, shiftMinutes],
  );

  const isDecisionNode = selectedCard?.tipo === 'decisao';
  const activityNode =
    selectedCard && !['card_inicial', 'card_final', 'decisao'].includes(selectedCard.tipo)
      ? selectedCard
      : null;

  const computeNodeLoad = useCallback(
    (card: CardData) => {
      const override = simulation.kaizenOverrides[card.id] ?? {};
      const extraFte = Math.max(0, Number(override.fte ?? override.extraFTE ?? 0));
      const reworkReduction = clamp(Number(override.rework ?? override.reworkReduction ?? 0), 0, 100);
      const baseFte = Math.max(1, Number(card.pessoasSimultaneas ?? 1));
      const baseCycleMinutes = isFactoryMap && (card as any).vsmIcon === 'processo'
        ? toFactoryMinutes((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit, 's')
        : Math.max(0, Number(card.tt ?? 0));
      const adjustedCycleMinutes = baseCycleMinutes * (1 - reworkReduction / 100);
      const capacity = adjustedCycleMinutes > 0 ? (shiftMinutes * (baseFte + extraFte)) / adjustedCycleMinutes : 0;
      const loadPercent = capacity > 0 ? (demand / capacity) * 100 : 999;
      const overflowPerDay = Math.max(0, Math.round(demand - capacity));
      return { loadPercent, overflowPerDay };
    },
    [demand, isFactoryMap, shiftMinutes, simulation.kaizenOverrides],
  );

  const operationKpis = useMemo(() => {
    const days = HORIZON_DAYS[horizon];
    if (processCards.length === 0) {
      return { leadTimeDays: 0, totalQueue: 0, costOfInertia: 0 };
    }

    const rows = processCards.map((card) => {
      const { loadPercent } = computeNodeLoad(card);
      const capacity = loadPercent > 0 ? demand / (loadPercent / 100) : 0;
      const queueAccumulated = Math.max(0, Math.round((demand - capacity) * days));
      const flowDays = capacity > 0 ? demand / capacity / (shiftMinutes / 60) : 1;
      return { queueAccumulated, flowDays };
    });

    const totalQueue = rows.reduce((sum, row) => sum + row.queueAccumulated, 0);
    const leadTimeDays = rows.reduce((sum, row) => sum + row.flowDays, 0) * Math.max(1, batchSize / 10);

    return {
      leadTimeDays: Math.round(leadTimeDays * 10) / 10,
      totalQueue,
      costOfInertia: totalQueue * TICKET_MEDIO,
    };
  }, [batchSize, computeNodeLoad, demand, horizon, processCards, shiftMinutes]);

  useEffect(() => {
    if (!insightSuggestion) return;
    if (lastAppliedSuggestionRef.current === insightSuggestion.id) return;

    lastAppliedSuggestionRef.current = insightSuggestion.id;

    const normalize = (value: string | undefined | null) =>
      (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

    if (!suggestionBaselineOverridesRef.current) {
      suggestionBaselineOverridesRef.current = { ...simulation.kaizenOverrides };
    }

    const beforeOverrides = { ...(suggestionBaselineOverridesRef.current ?? {}) };
    const beforeMetrics = calculateSnapshotMetrics(beforeOverrides);
    const extraFte = clamp(Math.round(insightSuggestion.suggestedExtraFte ?? 1), 0, 50);
    const waitReduction = clamp(Math.round(insightSuggestion.suggestedWaitReductionPercent ?? 20), 0, 100);

    const focusCard =
      processCards.find((card) => card.id === insightSuggestion.focusCardId) ??
      processCards.find((card) => {
        const focus = normalize(insightSuggestion.focusResource);
        if (!focus) return false;
        const resource = normalize(card.quem || card.responsavel || card.atividade || '');
        return resource.includes(focus) || focus.includes(resource);
      }) ??
      null;

    if (!focusCard) {
      onOptimizationFeedback?.('warning', 'Recurso do insight não encontrado para simulação assistida.');
      return;
    }

    const current = normalizeOverride(beforeOverrides[focusCard.id]);
    const nextOverride = {
      fte: clamp(current.fte + extraFte, 0, 50),
      rework: Math.max(current.rework, waitReduction),
    };

    const afterOverrides = {
      ...beforeOverrides,
      [focusCard.id]: nextOverride,
    };

    simulation.setKaizenOverrides(afterOverrides);
    setOptimizedOverridesSnapshot(afterOverrides);

    const afterMetrics = calculateSnapshotMetrics(afterOverrides);
    const monthlyWorkedHours = (shiftMinutes / 60) * 22;
    const netGain = Math.max(0, (beforeMetrics.nvaCost - afterMetrics.nvaCost) * monthlyWorkedHours);
    const investment = Math.max(0, extraFte * 4500);
    const estimatedRoiPercent =
      investment > 0 ? ((netGain - investment) / investment) * 100 : netGain > 0 ? 999 : 0;

    setOptimizationSummary({
      before: beforeMetrics,
      after: afterMetrics,
      estimatedRoiPercent,
      netGain,
      investment,
      generatedAt: new Date().toISOString(),
      executionMs: 0,
    });

    onFocusBottleneck?.(focusCard.id);
    onOptimizationFeedback?.('success', `Sugestão aplicada no simulador para ${focusCard.quem || focusCard.atividade || 'recurso selecionado'}.`);
  }, [
    calculateSnapshotMetrics,
    insightSuggestion,
    onFocusBottleneck,
    onOptimizationFeedback,
    processCards,
    shiftMinutes,
    simulation,
  ]);

  useEffect(() => {
    simulation.setCurrentKpis(operationKpis.leadTimeDays, operationKpis.costOfInertia);
    simulation.ensureBaseline(operationKpis.leadTimeDays, operationKpis.costOfInertia);
  }, [operationKpis.costOfInertia, operationKpis.leadTimeDays, simulation]);

  const leadTimeDelta = useMemo(() => {
    if (simulation.baselineLeadTime === null) return 0;
    return simulation.baselineLeadTime - operationKpis.leadTimeDays;
  }, [operationKpis.leadTimeDays, simulation.baselineLeadTime]);

  const inertiaDelta = useMemo(() => {
    if (simulation.baselineCost === null) return 0;
    return simulation.baselineCost - operationKpis.costOfInertia;
  }, [operationKpis.costOfInertia, simulation.baselineCost]);

  const selectedNodeState = useMemo(() => {
    if (!activityNode) return null;
    return computeNodeLoad(activityNode);
  }, [activityNode, computeNodeLoad]);

  const decisionMix = useMemo(() => {
    if (!isDecisionNode || !selectedCard) return null;
    const contextMix = simulation.decisionMixOverrides[selectedCard.id];
    const simPct = clamp(Number(contextMix?.simPct ?? selectedCard.decisao_sim_pct ?? 50), 0, 100);
    return {
      simPct,
      naoPct: 100 - simPct,
    };
  }, [isDecisionNode, selectedCard, simulation.decisionMixOverrides]);

  const updateDecisionMix = useCallback(
    (nextSimPct: number) => {
      if (!selectedCard || selectedCard.tipo !== 'decisao') return;
      const simPct = clamp(Math.round(nextSimPct), 0, 100);
      const naoPct = 100 - simPct;
      simulation.setDecisionMixOverride(selectedCard.id, { simPct, naoPct });
      if (viewMode === 'kaizen_work') {
        onUpdateCardData(selectedCard.id, {
          decisao_sim_pct: simPct,
          decisao_nao_pct: naoPct,
        } as Partial<CardData>);
      }
    },
    [onUpdateCardData, selectedCard, simulation, viewMode],
  );

  const updateSelectedOverride = useCallback(
    (patch: Partial<{ extraFte: number; reworkReduction: number }>) => {
      if (!activityNode || !selectedNodeState) return;
      const current = simulation.kaizenOverrides[activityNode.id] ?? {};
      const currentFte = Math.max(0, Number(current.fte ?? current.extraFTE ?? 0));
      const currentRework = clamp(Number(current.rework ?? current.reworkReduction ?? 0), 0, 100);
      simulation.setKaizenOverride(activityNode.id, {
        fte: clamp(patch.extraFte ?? currentFte, 0, 50),
        rework: clamp(patch.reworkReduction ?? currentRework, 0, 100),
      });
    },
    [activityNode, selectedNodeState, simulation],
  );

  const handleFocusBottleneck = useCallback(() => {
    if (!onFocusBottleneck || processCards.length === 0) return;
    const worst = processCards
      .map((card) => ({ card, ...computeNodeLoad(card) }))
      .sort((a, b) => b.loadPercent - a.loadPercent)[0];
    if (worst?.card?.id) onFocusBottleneck(worst.card.id);
  }, [computeNodeLoad, onFocusBottleneck, processCards]);

  const handleSaveScenario = useCallback(() => {
    const scenarioName = `Cenário ${simulation.savedScenarios.length + 1}`;
    const kpis = calculateSnapshotMetrics(simulation.kaizenOverrides);
    const nodeSnapshot = processCards.map((card) => {
      const baseFte = Math.max(1, Number(card.pessoasSimultaneas ?? card.p ?? 1));
      const override = simulation.kaizenOverrides[card.id] ?? {};
      const extraFte = Math.max(0, Number(override.fte ?? override.extraFTE ?? 0));
      const { tra: traMinutes, tp: tpMinutes } = getNodeTimes(card, isFactoryMap);
      return {
        id: card.id,
        name: card.atividade || card.quem || card.responsavel || 'Etapa',
        traMinutes,
        tpMinutes,
        baseFte,
        extraFte,
        totalFte: baseFte + extraFte,
      };
    });
    const scenarioId = simulation.saveScenario(scenarioName, {
      source: 'manual',
      isMilestone: true,
      totalLeadTime: kpis.leadTimeMinutes,
      inertiaCost: kpis.nvaCost,
      kpis,
      nodeSnapshot,
    });
    if (scenarioId) {
      onScenarioSaved?.(scenarioName);
    }
  }, [
    calculateSnapshotMetrics,
    isFactoryMap,
    onScenarioSaved,
    processCards,
    simulation,
  ]);

  const handleAutoOptimize = useCallback(() => {
    if (isOptimizing) return;
    if (processCards.length === 0) {
      onOptimizationFeedback?.('warning', 'Nenhuma etapa ativa para otimizar.');
      return;
    }

    setIsOptimizing(true);

    try {
      const currentOverridesSnapshot = { ...simulation.kaizenOverrides };
      const beforeMetrics = calculateSnapshotMetrics(currentOverridesSnapshot);

      const result = optimizeWorkflow({
        nodes: processCards.map((card) => ({
          id: card.id,
          name: card.atividade || card.quem || card.responsavel || 'Etapa',
          traMinutes: getNodeTimes(card, isFactoryMap).tra,
          tpMinutes: getNodeTimes(card, isFactoryMap).tp,
          baseOperators: Math.max(1, Number(card.pessoasSimultaneas ?? 1)),
          hourlyCost: Number(card.hourlyCost ?? 0),
        })),
        demand,
        shiftMinutes,
        batchSize,
        horizonDays: HORIZON_DAYS[horizon],
        ticketAverage: TICKET_MEDIO,
        currentOverrides: simulation.kaizenOverrides,
        maxIterations: 220,
        waitReductionStepPercent: 20,
        targetMinLoadPercent: 85,
        targetMaxLoadPercent: 95,
        });

      const optimizedOverridesSnapshot = {
        ...currentOverridesSnapshot,
        ...result.optimizedOverrides,
      };
      const afterMetrics = calculateSnapshotMetrics(optimizedOverridesSnapshot);
      setOptimizedOverridesSnapshot(optimizedOverridesSnapshot);

      Object.entries(result.optimizedOverrides).forEach(([nodeId, override]) => {
        simulation.setKaizenOverride(nodeId, override);
      });

      // Força refresh de estados visuais de simulação (heatmap e badges de WIP nas conexões).
      const overloadedIds = new Set(
        result.nodeStatus.filter((node) => node.loadPercent > 100).map((node) => node.id),
      );
      simulation.setOverloadedCardIds(overloadedIds);
      simulation.setCurrentKpis(result.leadTimeAfterDays, result.costAfter);

      const monthlyWorkedHours = (shiftMinutes / 60) * 22;
      const netGain = Math.max(0, (beforeMetrics.nvaCost - afterMetrics.nvaCost) * monthlyWorkedHours);
      const estimatedRoiPercent =
        result.addedInvestment > 0
          ? ((netGain - result.addedInvestment) / result.addedInvestment) * 100
          : netGain > 0
          ? 999
          : 0;

      setOptimizationSummary({
        before: beforeMetrics,
        after: afterMetrics,
        estimatedRoiPercent,
        netGain,
        investment: result.addedInvestment,
        generatedAt: new Date().toISOString(),
        executionMs: result.executionMs,
      });

      const reduction = Math.max(0, result.leadTimeReductionPercent);
      const messageCore = `Redução de ${reduction.toFixed(1)}% no Lead Time com investimento de ${fmtCurrency(
        result.addedInvestment,
      )}.`;

      if (reduction <= 0 && netGain <= 0) {
        onOptimizationFeedback?.(
          'warning',
          `Fluxo já otimizado para a demanda atual de ${demand} pedidos. Tente reduzir Recursos (FTE) ou aumentar a Demanda para ver novos ganhos.`,
        );
        return;
      }

      if (result.isBalanced) {
        onOptimizationFeedback?.('success', `Estado Ótimo Atingido: ${messageCore}`);
      } else {
        onOptimizationFeedback?.(
          'warning',
          `Otimização parcial (${result.iterations} ciclos): ${messageCore}`,
        );
      }
    } catch (error) {
      console.error('[SimulationSidebar] Falha ao otimizar workflow:', error);
      onOptimizationFeedback?.('error', 'Falha ao executar a auto-otimização.');
    } finally {
      setIsOptimizing(false);
    }
  }, [
    calculateSnapshotMetrics,
    batchSize,
    demand,
    horizon,
    isFactoryMap,
    isOptimizing,
    onOptimizationFeedback,
    processCards,
    shiftMinutes,
    simulation,
  ]);

  const handleGenerateExecutiveReport = useCallback(async () => {
    if (!optimizationSummary) {
      onOptimizationFeedback?.('warning', 'Nenhuma otimização para exportar.');
      return;
    }

    try {
      const criticalBaseline = processCards.reduce<{
        name: string;
        loadPercent: number;
      } | null>((worst, card) => {
        const traMinutes = Math.max(0.01, getNodeTimes(card, isFactoryMap).tra || 0.01);
        const baseFte = Math.max(1, Number(card.pessoasSimultaneas ?? 1));
        const capacity = (shiftMinutes * baseFte) / traMinutes;
        const loadPercent = capacity > 0 ? (demand / capacity) * 100 : 999;
        const name = card.atividade || card.quem || card.responsavel || card.cargo || 'Recurso';
        if (!worst || loadPercent > worst.loadPercent) return { name, loadPercent };
        return worst;
      }, null);

      const beforeWaitMinutes =
        optimizationSummary.before.leadTimeMinutes * (1 - optimizationSummary.before.pcePercent / 100);
      const afterWaitMinutes =
        optimizationSummary.after.leadTimeMinutes * (1 - optimizationSummary.after.pcePercent / 100);
      const waitRemovedMinutes = Math.max(0, beforeWaitMinutes - afterWaitMinutes);

      const taktTimeMinutes = Math.max(0, Number(metrics?.taktTime ?? 0));
      const simulationId = `SIM-${new Date(optimizationSummary.generatedAt).toISOString().slice(0, 10)}`;

      await generateExecutiveReport({
        simulationId,
        generatedAtIso: optimizationSummary.generatedAt,
        statusLabel: 'VALIDADO VIA ENGINE LEAN AI',
        before: optimizationSummary.before,
        after: optimizationSummary.after,
        estimatedRoiPercent: optimizationSummary.estimatedRoiPercent,
        projectedSavings: Math.max(
          0,
          optimizationSummary.before.nvaCost - optimizationSummary.after.nvaCost,
        ),
        criticalResourceName: criticalBaseline?.name || 'Recurso critico',
        criticalLoadPercent: Number.isFinite(criticalBaseline?.loadPercent)
          ? (criticalBaseline?.loadPercent as number)
          : 0,
        taktTimeMinutes: Number.isFinite(taktTimeMinutes) ? taktTimeMinutes : 0,
        waitRemovedMinutes,
        variabilityMinutes: 17.2,
      });

      onOptimizationFeedback?.('success', 'Relatório executivo em PDF gerado.');
    } catch (error) {
      console.error('[SimulationSidebar] Falha ao gerar relatório executivo:', error);
      onOptimizationFeedback?.('error', 'Falha ao gerar relatório executivo em PDF.');
    }
  }, [demand, isFactoryMap, metrics?.taktTime, onOptimizationFeedback, optimizationSummary, processCards, shiftMinutes]);

  const handleApplyOptimizationOnMap = useCallback(() => {
    if (!optimizationSummary) {
      onOptimizationFeedback?.('warning', 'Nenhuma otimização disponível para aplicar.');
      return;
    }

    const leadTimeGainPercent =
      optimizationSummary.before.leadTimeMinutes > 0
        ? ((optimizationSummary.before.leadTimeMinutes - optimizationSummary.after.leadTimeMinutes) /
            optimizationSummary.before.leadTimeMinutes) *
          100
        : 0;

    simulation.saveScenario(`Marco Otimizado ${new Date().toLocaleDateString('pt-BR')}`, {
      source: 'optimization',
      isMilestone: true,
      totalLeadTime: optimizationSummary.after.leadTimeMinutes,
      inertiaCost: optimizationSummary.after.nvaCost,
      leadTimeGainPercent,
      kpis: {
        leadTimeMinutes: optimizationSummary.after.leadTimeMinutes,
        pcePercent: optimizationSummary.after.pcePercent,
        nvaCost: optimizationSummary.after.nvaCost,
        deliveryCapacityPerDay: optimizationSummary.after.deliveryCapacityPerDay,
      },
    });

    onOptimizationFeedback?.(
      'success',
      'Cenário registrado como marco. Baseline preservado; use confirmação explícita para sobrescrever baseline.',
    );
  }, [onOptimizationFeedback, optimizationSummary, simulation]);

  const handleApplySnapshot = useCallback(
    (snapshotId: string) => {
      const scenario = simulation.savedScenarios.find((item) => item.id === snapshotId);
      simulation.loadScenario(snapshotId);
      if (scenario) {
        setHorizon(SIM_TO_HORIZON[scenario.horizon] ?? '1m');
      }
      setSnapshotOpen(false);
    },
    [simulation],
  );

  const analyticsKpis = useMemo(() => {
    const pce = Number(metrics?.flowEfficiency ?? 0);
    const totalTT = Number(metrics?.totalTT ?? 0);
    const velocityPerHour = totalTT > 0 ? 60 / totalTT : 0;
    const operationalCost = Number(metrics?.monthlyOpCost ?? 0);
    const transactionsPerMonth = Math.max(1, demand * 22);
    const costPerTransaction = operationalCost / transactionsPerMonth;

    const rtyCards = processCards.filter((card) => card.tipo === 'atividade');
    const rty = rtyCards.length
      ? rtyCards.reduce((acc, card) => {
          const accuracy = Number((card as any).percentLivreErros ?? card.assertividade ?? 98);
          return (acc * clamp(accuracy, 0, 100)) / 100;
        }, 1) * 100
      : 100;

    return {
      pce,
      rty,
      velocityPerHour,
      operationalCost,
      costPerTransaction,
    };
  }, [demand, metrics, processCards]);

  const yamazumiData = useMemo(() => {
    return processCards.slice(0, 5).map((card) => ({
      id: card.id,
      label: (card.quem || card.atividade || 'Etapa').slice(0, 12),
      loadPercent: Math.max(0, computeNodeLoad(card).loadPercent),
    }));
  }, [computeNodeLoad, processCards]);

  const yamazumiScale = useMemo(() => {
    const maxLoad = Math.max(100, ...yamazumiData.map((item) => item.loadPercent));
    const chartMax = Math.ceil(maxLoad / 10) * 10;
    const taktTopPercent = clamp(100 - (100 / chartMax) * 100, 0, 100);
    return { chartMax, taktTopPercent };
  }, [yamazumiData]);

  const selectedLoadTheme = useMemo(() => {
    if (!selectedNodeState) return null;
    if (selectedNodeState.loadPercent > 150) return 'text-red-700 bg-red-50 border-red-200';
    if (selectedNodeState.loadPercent > 100) return 'text-orange-700 bg-orange-50 border-orange-200';
    if (selectedNodeState.loadPercent >= 90) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  }, [selectedNodeState]);

  const savedMilestones = useMemo(
    () => simulation.savedScenarios.filter((item) => item.isMilestone).slice(0, 4),
    [simulation.savedScenarios],
  );

  return (
    <aside className="simulation-sidebar w-full h-full bg-slate-50 dark:bg-slate-900 border-l border-slate-300 dark:border-slate-700 shadow-[-4px_0_15px_rgba(0,0,0,0.05)] z-10 flex flex-col">
      <div className="h-14 px-3 border-b border-slate-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-600" />
          <h2 className="text-sm font-semibold text-slate-700">Simulador What-If</h2>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <button
              type="button"
              onClick={() => setSnapshotOpen((prev) => !prev)}
              className="h-7 px-2 rounded border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 flex items-center gap-1"
              title="Cenários salvos"
            >
              <Camera className="w-3.5 h-3.5 text-slate-500" />
              Cenários
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>
            {snapshotOpen && (
              <div className="absolute right-0 mt-1 w-64 max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-30 p-1">
                {simulation.savedScenarios.length === 0 ? (
                  <div className="px-2 py-1.5 text-[11px] text-slate-500">Nenhum cenário salvo.</div>
                ) : (
                  simulation.savedScenarios.map((item) => (
                    <div key={item.id} className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleApplySnapshot(item.id)}
                        className="flex-1 text-left px-2 py-1.5 rounded text-[11px] hover:bg-slate-50"
                      >
                        <div className="font-semibold text-slate-700 truncate">{item.name}</div>
                        <div className="text-slate-500">{fmtCurrency(item.inertiaCost)} | {item.totalLeadTime.toFixed(1)}d</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => simulation.deleteScenario(item.id)}
                        className="h-6 w-6 rounded border border-slate-200 text-slate-400 hover:text-red-600"
                        title="Excluir cenário"
                      >
                        x
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleSaveScenario}
            className="h-7 px-2 rounded border border-slate-300 bg-white text-[11px] font-semibold text-slate-700"
            title="Salvar cenário atual"
          >
            Salvar
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded border border-slate-200 bg-white text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center"
            title="Encerrar simulação"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="h-10 px-2 border-b border-slate-200 bg-slate-100/70 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setActiveTab('operation')}
          className={`flex-1 h-7 rounded-md text-xs font-semibold ${
            activeTab === 'operation' ? 'bg-white border border-slate-200 text-slate-800' : 'text-slate-500 dark:text-slate-300'
          }`}
        >
          Operação
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('analytics')}
          className={`flex-1 h-7 rounded-md text-xs font-semibold ${
            activeTab === 'analytics' ? 'bg-white border border-slate-200 text-slate-800' : 'text-slate-500 dark:text-slate-300'
          }`}
        >
          Analytics
        </button>
      </div>

      <div className="border-b border-slate-200 bg-white px-3 py-2">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Marcos Salvos</span>
          <span className="text-[10px] font-mono tabular-nums text-slate-400">{savedMilestones.length}</span>
        </div>
        {savedMilestones.length === 0 ? (
          <div className="text-[10px] text-slate-500">Nenhum marco registrado.</div>
        ) : (
          <div className="space-y-1">
            {savedMilestones.map((item) => (
              <div key={item.id} className="grid grid-cols-[1fr_auto] gap-2 border border-slate-200 bg-slate-50 px-2 py-1">
                <div className="min-w-0">
                  <div className="truncate text-[10px] font-semibold text-slate-700">{item.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString('pt-BR') : 'Sem data'}
                  </div>
                </div>
                <div className="self-center text-right text-[10px]">
                  <div className="font-mono tabular-nums text-slate-700">
                    {item.leadTimeGainPercent !== undefined ? `${item.leadTimeGainPercent.toFixed(1)}%` : '--'}
                  </div>
                  <div className="text-slate-500">ganho LT</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeTab === 'operation' && (
        <div className="flex-1 min-h-0 overflow-hidden p-3 flex flex-col gap-6">
          <section className="rounded border border-slate-200 bg-white p-3 space-y-4 shrink-0 border-b border-slate-200 py-4">
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-semibold text-slate-700 min-w-16">Demanda</label>
              <input
                type="range"
                min={1}
                max={1000}
                value={demand}
                onChange={(e) => setGlobalDemand(Number(e.target.value))}
                className="flex-1 h-1.5 accent-blue-600"
              />
              <input
                type="number"
                min={1}
                max={10000}
                value={demand}
                onChange={(e) => setGlobalDemand(Number(e.target.value))}
                className="h-7 w-16 rounded-md border border-slate-300 bg-white px-1.5 text-xs font-semibold text-slate-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setHorizonOpen((open) => !open)}
                  className="h-7 w-full px-2 rounded-md border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 flex items-center justify-between"
                >
                  <span>{HORIZON_LABELS[horizon]}</span>
                  <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>
                {horizonOpen && (
                  <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1">
                    {(Object.keys(HORIZON_LABELS) as Horizon[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setHorizon(option);
                          simulation.setHorizon(HORIZON_TO_SIM[option]);
                          setHorizonOpen(false);
                        }}
                        className={`w-full text-left px-2 py-1 text-xs hover:bg-slate-50 ${
                          option === horizon ? 'text-blue-600 font-semibold' : 'text-slate-700'
                        }`}
                      >
                        {HORIZON_LABELS[option]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShiftOpen((open) => !open)}
                  className="h-7 w-full px-2 rounded-md border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 flex items-center justify-between"
                >
                  <span>{SHIFT_LABELS[simulation.shiftPreset]}</span>
                  <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>
                {shiftOpen && (
                  <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1">
                    {(Object.keys(SHIFT_LABELS) as SimulationShiftPreset[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          simulation.setShiftPreset(option);
                          setShiftOpen(false);
                        }}
                        className={`w-full text-left px-2 py-1 text-xs hover:bg-slate-50 ${
                          option === simulation.shiftPreset ? 'text-blue-600 font-semibold' : 'text-slate-700'
                        }`}
                      >
                        {SHIFT_LABELS[option]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-[11px] font-semibold text-slate-700 min-w-16">Batch</label>
              <input
                type="number"
                min={1}
                max={999}
                value={batchSize}
                onChange={(e) => setGlobalBatchSize(Number(e.target.value))}
                className="h-7 w-16 rounded-md border border-slate-300 bg-white px-1.5 text-xs font-semibold text-slate-700"
              />
              <span className="text-[10px] text-slate-500">itens/lote</span>
            </div>
          </section>

          <div className="flex items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={handleAutoOptimize}
              disabled={isOptimizing}
              className="h-7 px-2 rounded border border-slate-300 bg-white text-[11px] font-semibold text-emerald-700 hover:bg-slate-50 flex items-center gap-1 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              title="Ajustar automaticamente FTE para manter ocupação entre 85% e 95% do Takt"
            >
              {isOptimizing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Activity className="w-3.5 h-3.5" />
              )}
              {isFactoryMap ? 'Balancear Capacidade' : 'Auto-Balancear FTE'}
            </button>
            <button
              type="button"
              onClick={handleFocusBottleneck}
              className="h-7 px-2 rounded border border-slate-300 bg-white text-[11px] font-semibold text-blue-600 hover:bg-slate-50 flex items-center gap-1 shadow-sm"
              title="Focar gargalo"
            >
              <Radar className="w-3.5 h-3.5" />
              Focar Gargalo
            </button>
          </div>

          <section className="grid grid-cols-2 gap-3 shrink-0 items-stretch">
            <KpiCard
              icon={<Clock className="w-3.5 h-3.5 text-slate-400" />}
              label={isFactoryMap ? 'PLT Simulado' : 'Lead Time'}
              value={`${operationKpis.leadTimeDays}d`}
              delta={leadTimeDelta > 0 ? `â†“ Menos ${leadTimeDelta.toFixed(1)} dias` : undefined}
            />
            <KpiCard
              icon={<DollarSign className="w-3.5 h-3.5 text-red-500" />}
              label={isFactoryMap ? 'Custo Estoque/NVA' : 'Custo Inércia'}
              value={fmtCurrency(operationKpis.costOfInertia)}
              valueClassName="text-red-600"
              delta={inertiaDelta > 0 ? `â†“ Economia de ${fmtCurrency(inertiaDelta)}` : undefined}
            />
          </section>

          {optimizationSummary && (
            <OptimizationSummaryCard
              data={optimizationSummary}
              onGenerateReport={handleGenerateExecutiveReport}
              onApplyMap={handleApplyOptimizationOnMap}
            />
          )}

          <section className="flex-1 min-h-0 rounded border border-slate-200 bg-white p-3 overflow-hidden">
            {!selectedCard && (
              <div className="h-full flex flex-col items-center justify-center text-center text-[11px] text-slate-500">
                <Target className="w-4 h-4 mb-1.5 text-slate-400" />
                Selecione uma etapa ou decisão no mapa...
              </div>
            )}

            {activityNode && selectedNodeState && (
              <div className="h-full flex flex-col gap-4">
                <div>
                  <p className="text-[11px] font-semibold text-slate-700 truncate">{activityNode.atividade || 'Etapa sem nome'}</p>
                  <p className="text-[10px] text-slate-500 truncate">{activityNode.quem || activityNode.responsavel || 'Não atribuído'}</p>
                </div>

                <div className={`rounded border p-2 ${selectedLoadTheme}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{isFactoryMap ? 'Ocupação vs Takt' : 'Carga'}</span>
                    <span className="text-base font-medium leading-none">{selectedNodeState.loadPercent.toFixed(0)}%</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-current"
                      style={{ width: `${Math.min(100, selectedNodeState.loadPercent)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-[10px]">{isFactoryMap ? 'Déficit capacidade' : 'Impacto'}: +{selectedNodeState.overflowPerDay} itens/dia.</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-400" /> {isFactoryMap ? 'Operadores' : 'FTE'}</span>
                    <div className="border border-slate-300 rounded-md flex items-center overflow-hidden bg-white">
                      <button
                        type="button"
                        onClick={() => {
                          const current = simulation.kaizenOverrides[activityNode.id] ?? {};
                          const currentFte = Math.max(0, Number(current.fte ?? current.extraFTE ?? 0));
                          updateSelectedOverride({ extraFte: Math.max(0, currentFte - 1) });
                        }}
                        className="bg-slate-50 hover:bg-slate-100 px-3 py-1 text-blue-600 font-semibold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center text-[11px] font-semibold text-slate-700 tabular-nums bg-white">+
                        {Math.max(0, Number((simulation.kaizenOverrides[activityNode.id]?.fte ?? simulation.kaizenOverrides[activityNode.id]?.extraFTE ?? 0)))}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const current = simulation.kaizenOverrides[activityNode.id] ?? {};
                          const currentFte = Math.max(0, Number(current.fte ?? current.extraFTE ?? 0));
                          updateSelectedOverride({ extraFte: currentFte + 1 });
                        }}
                        className="bg-slate-50 hover:bg-slate-100 px-3 py-1 text-blue-600 font-semibold"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5 text-slate-400" /> {isFactoryMap ? 'Reduzir CT' : 'Retrab.'}</span>
                    <div className="border border-slate-300 rounded-md flex items-center overflow-hidden bg-white">
                      <button
                        type="button"
                        onClick={() => {
                          const current = simulation.kaizenOverrides[activityNode.id] ?? {};
                          const currentRework = clamp(Number(current.rework ?? current.reworkReduction ?? 0), 0, 100);
                          updateSelectedOverride({ reworkReduction: Math.max(0, currentRework - 10) });
                        }}
                        className="bg-slate-50 hover:bg-slate-100 px-3 py-1 text-blue-600 font-semibold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center text-[11px] font-semibold text-slate-700 tabular-nums bg-white">-
                        {clamp(Number(simulation.kaizenOverrides[activityNode.id]?.rework ?? simulation.kaizenOverrides[activityNode.id]?.reworkReduction ?? 0), 0, 100)}%
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const current = simulation.kaizenOverrides[activityNode.id] ?? {};
                          const currentRework = clamp(Number(current.rework ?? current.reworkReduction ?? 0), 0, 100);
                          updateSelectedOverride({ reworkReduction: Math.min(100, currentRework + 10) });
                        }}
                        className="bg-slate-50 hover:bg-slate-100 px-3 py-1 text-blue-600 font-semibold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isDecisionNode && selectedCard && decisionMix && (
              <div className="h-full flex flex-col gap-2">
                <div>
                  <p className="text-[11px] font-semibold text-slate-700 truncate">{selectedCard.atividade || 'Decisão sem nome'}</p>
                  <p className="text-[10px] text-slate-500">Ajuste do mix de produtos no split</p>
                </div>

                <div className="rounded border border-slate-200 bg-slate-50 p-2 space-y-2">
                  <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700">
                    <span>SIM</span>
                    <span>{decisionMix.simPct}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={decisionMix.simPct}
                    onChange={(e) => updateDecisionMix(Number(e.target.value))}
                    className="w-full h-1.5 accent-blue-600"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[10px] text-slate-600">Sim (%)
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={decisionMix.simPct}
                        onChange={(e) => updateDecisionMix(Number(e.target.value))}
                        className="mt-1 h-7 w-full rounded border border-slate-300 px-1.5 text-xs font-semibold text-slate-700"
                      />
                    </label>
                    <label className="text-[10px] text-slate-600">Não (%)
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={decisionMix.naoPct}
                        onChange={(e) => updateDecisionMix(100 - Number(e.target.value))}
                        className="mt-1 h-7 w-full rounded border border-slate-300 px-1.5 text-xs font-semibold text-slate-700"
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
          <AnalyticsCard
            label="Eficiência PCE"
            value={`${analyticsKpis.pce.toFixed(1)}%`}
            icon={<Gauge className="w-4 h-4 text-slate-400" />}
            progress={clamp(analyticsKpis.pce, 0, 100)}
            tone={analyticsKpis.pce > 20 ? 'good' : analyticsKpis.pce < 10 ? 'danger' : 'warn'}
          />
          <AnalyticsCard
            label="RTY - First Pass"
            value={`${analyticsKpis.rty.toFixed(1)}%`}
            icon={<Target className="w-4 h-4 text-slate-400" />}
            progress={clamp(analyticsKpis.rty, 0, 100)}
            tone={analyticsKpis.rty >= 95 ? 'good' : analyticsKpis.rty < 85 ? 'danger' : 'warn'}
          />
          <AnalyticsCard
            label="Velocidade"
            value={`${analyticsKpis.velocityPerHour.toFixed(2)} itens/h`}
            icon={<Layers className="w-4 h-4 text-slate-400" />}
            progress={clamp(analyticsKpis.velocityPerHour * 10, 0, 100)}
            tone="good"
          />
          <AnalyticsCard
            label="Custo Operacional"
            value={fmtCurrency(analyticsKpis.operationalCost)}
            icon={<DollarSign className="w-4 h-4 text-slate-400" />}
            progress={0}
            tone="neutral"
          />
          <AnalyticsCard
            label="Custo por Transação"
            value={fmtCurrency(analyticsKpis.costPerTransaction)}
            icon={<AlertTriangle className="w-4 h-4 text-slate-400" />}
            progress={0}
            tone="neutral"
          />

          <section className="rounded border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold text-slate-700 mb-2">Balanceamento (Yamazumi)</div>
            {yamazumiData.length === 0 ? (
              <div className="text-[11px] text-slate-500">Sem etapas suficientes para análise.</div>
            ) : (
              <div className="relative h-40 rounded-md border border-slate-100 bg-slate-50 px-2 pb-2 pt-3">
                <div
                  className="absolute left-2 right-2 border-t border-dashed border-red-400"
                  style={{ top: `${yamazumiScale.taktTopPercent}%` }}
                />
                <div className="h-full flex items-end justify-between gap-2">
                  {yamazumiData.map((bar) => {
                    const capped = Math.min(bar.loadPercent, yamazumiScale.chartMax);
                    const rawHeight = (capped / yamazumiScale.chartMax) * 100;
                    const heightPct = bar.loadPercent > 0 ? Math.max(12, rawHeight) : 0;
                    const barClass =
                      bar.loadPercent > 100 ? 'bg-orange-500 border-orange-600' : 'bg-emerald-500 border-emerald-600';
                    return (
                      <div key={bar.id} className="flex-1 min-w-0 flex flex-col items-center gap-1">
                        <div className="text-[10px] font-semibold text-slate-600 tabular-nums leading-none">{Math.round(bar.loadPercent)}%</div>
                        <div className="w-full h-28 flex items-end">
                          <div
                            className={`w-full rounded-t-sm border ${barClass}`}
                            style={{ height: `${heightPct}%` }}
                          />
                        </div>
                        <div className="w-full truncate text-center text-[10px] text-slate-500 leading-none">{bar.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </aside>
  );
};

const KpiCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClassName?: string;
  delta?: string;
}> = ({ icon, label, value, valueClassName, delta }) => (
  <div className="h-14 rounded-md bg-slate-50 dark:bg-slate-800/50 px-2 py-1.5 shadow-sm">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
      {icon}
      {label}
    </div>
    <div className={`mt-0.5 text-sm font-medium text-slate-800 dark:text-slate-100 tabular-nums ${valueClassName ?? ''}`}>{value}</div>
    {delta && <div className="text-[9px] font-semibold text-emerald-600 truncate">{delta}</div>}
  </div>
);

const AnalyticsCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  progress: number;
  tone: 'good' | 'warn' | 'danger' | 'neutral';
}> = ({ label, value, icon, progress, tone }) => {
  const toneClass =
    tone === 'good'
      ? 'bg-emerald-500'
      : tone === 'danger'
      ? 'bg-red-500'
      : tone === 'warn'
      ? 'bg-amber-500'
      : 'bg-slate-300';

  return (
    <div className="rounded-md bg-slate-50 dark:bg-slate-800/50 p-2 shadow-sm">
      <div className="flex items-center justify-between text-[11px] font-semibold text-slate-600 dark:text-slate-300">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-0.5 text-sm font-medium text-slate-800 dark:text-slate-100 tabular-nums">{value}</div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full ${toneClass}`} style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
    </div>
  );
};

export default SimulationSidebar;

